import { z } from 'zod';
import { isoDaySchema } from './dates';

// ---------------------------------------------------------------------------------------------
// Orçamento — a lógica da planilha (T-182, RF-30, PRD §12; DEC-065)

export const BUDGET_TYPES = ['OPEX', 'CAPEX'] as const;
export type BudgetTypeName = (typeof BUDGET_TYPES)[number];
export const BUDGET_TYPE_LABELS: Record<BudgetTypeName, string> = { OPEX: 'Opex', CAPEX: 'Capex' };

/** Situação da vigência no dia de hoje. */
export const BUDGET_PERIOD_STATUSES = ['NOT_STARTED', 'ACTIVE', 'ENDED'] as const;
export type BudgetPeriodStatusName = (typeof BUDGET_PERIOD_STATUSES)[number];
export const BUDGET_PERIOD_STATUS_LABELS: Record<BudgetPeriodStatusName, string> = {
  NOT_STARTED: 'A iniciar',
  ACTIVE: 'Em vigência',
  ENDED: 'Encerrada',
};

/** Valor em reais: não negativo, até duas casas, cabe em DECIMAL(16,2). */
export const budgetValueSchema = z.coerce
  .number({ message: 'Informe o valor em números.' })
  .finite('Informe o valor em números.')
  .min(0, 'O valor não pode ser negativo.')
  .refine((v) => v < 1e14, 'Número grande demais.')
  .refine((v) => Number.isInteger(Math.round(v * 100 * 1e3) / 1e3), 'Use no máximo duas casas decimais.');

const r2 = (v: number) => Math.round(v * 100) / 100;

export const budgetItemInputSchema = z
  .object({
    name: z.string().trim().min(2, 'Informe o nome do item (2 ou mais letras).').max(160),
    description: z.string().trim().max(2000).nullable().default(null),
    category: z.string().trim().min(2, 'Informe a categoria.').max(80),
    type: z.enum(BUDGET_TYPES, { message: 'Escolha Opex ou Capex.' }),
    startDate: isoDaySchema,
    endDate: isoDaySchema,
    /** Vínculo opcional: uma iniciativa ou um objetivo (não os dois). */
    initiativeId: z.string().uuid().nullable().default(null),
    objectiveId: z.string().uuid().nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.endDate < v.startDate) {
      ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'O fim da vigência vem depois do início.' });
    }
    if (v.initiativeId && v.objectiveId) {
      ctx.addIssue({
        code: 'custom',
        path: ['objectiveId'],
        message: 'Vincule a uma iniciativa ou a um objetivo, não aos dois.',
      });
    }
  });
export type BudgetItemInput = z.input<typeof budgetItemInputSchema>;

/** Valores mensais de um ano (planejado e realizado; null = sem valor). */
export const budgetMonthsInputSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  months: z
    .array(
      z.object({
        month: z.number().int().min(1).max(12),
        planned: budgetValueSchema.nullable(),
        actual: budgetValueSchema.nullable(),
      }),
    )
    .min(1)
    .max(12)
    .refine((m) => new Set(m.map((x) => x.month)).size === m.length, 'O mesmo mês aparece duas vezes.'),
  reason: z.string().trim().max(500).optional(),
});

export type BudgetMonthValue = { year: number; month: number; planned: number | null; actual: number | null };

const sum = (values: (number | null)[]) => r2(values.reduce<number>((s, v) => s + (v ?? 0), 0));
const percent = (actual: number, planned: number) =>
  planned > 0 ? Math.round((actual / planned) * 10000) / 100 : null;

const dayMs = 86_400_000;
const toDay = (iso: string) =>
  Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));

/** Meses (ano, mês) dentro da vigência. */
export function monthsInPeriod(startDate: string, endDate: string): { year: number; month: number }[] {
  const out: { year: number; month: number }[] = [];
  let y = Number(startDate.slice(0, 4));
  let m = Number(startDate.slice(5, 7));
  const endY = Number(endDate.slice(0, 4));
  const endM = Number(endDate.slice(5, 7));
  while (y < endY || (y === endY && m <= endM)) {
    out.push({ year: y, month: m });
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/**
 * Vigência no dia de hoje: dias restantes até o fim (contando o último dia; 0 depois do fim), dias totais e a
 * situação (a iniciar, em vigência, encerrada).
 */
export function budgetPeriod(startDate: string, endDate: string, today: string) {
  const start = toDay(startDate);
  const end = toDay(endDate);
  const now = toDay(today);
  const totalDays = Math.round((end - start) / dayMs) + 1;
  const status: BudgetPeriodStatusName = now < start ? 'NOT_STARTED' : now > end ? 'ENDED' : 'ACTIVE';
  const daysRemaining = status === 'ENDED' ? 0 : Math.round((end - Math.max(now, start)) / dayMs) + 1;
  return { status, totalDays, daysRemaining };
}

/**
 * A lógica da planilha para um item (PRD §12): mês a mês no ano — planejado, realizado, saldo do mês, acumulados e
 * saldo acumulado —, o total anual (planejado, realizado, % consumido, saldo disponível) e o total geral de toda a
 * vigência.
 */
export function budgetItemReport(values: BudgetMonthValue[], year: number) {
  const ofYear = (m: number) => values.find((v) => v.year === year && v.month === m);
  let plannedAcc = 0;
  let actualAcc = 0;
  const months = Array.from({ length: 12 }, (_, i) => {
    const v = ofYear(i + 1);
    const planned = v?.planned ?? null;
    const actual = v?.actual ?? null;
    plannedAcc = r2(plannedAcc + (planned ?? 0));
    actualAcc = r2(actualAcc + (actual ?? 0));
    return {
      month: i + 1,
      planned,
      actual,
      balance: planned === null && actual === null ? null : r2((planned ?? 0) - (actual ?? 0)),
      consumedPercent: planned === null || actual === null ? null : percent(actual, planned),
      plannedAccumulated: plannedAcc,
      actualAccumulated: actualAcc,
      balanceAccumulated: r2(plannedAcc - actualAcc),
    };
  });
  const annualPlanned = sum(months.map((m) => m.planned));
  const annualActual = sum(months.map((m) => m.actual));
  const totalPlanned = sum(values.map((v) => v.planned));
  const totalActual = sum(values.map((v) => v.actual));
  return {
    months,
    annual: {
      planned: annualPlanned,
      actual: annualActual,
      consumedPercent: percent(annualActual, annualPlanned),
      available: r2(annualPlanned - annualActual),
    },
    total: {
      planned: totalPlanned,
      actual: totalActual,
      consumedPercent: percent(totalActual, totalPlanned),
      available: r2(totalPlanned - totalActual),
    },
  };
}
export type BudgetItemReport = ReturnType<typeof budgetItemReport>;

/** Totais do ano por grupo (categoria ou tipo). */
export function budgetTotalsBy<K extends string>(
  items: { key: K; annual: { planned: number; actual: number } }[],
): { key: K; planned: number; actual: number; consumedPercent: number | null; available: number }[] {
  const groups = new Map<K, { planned: number; actual: number }>();
  for (const i of items) {
    const g = groups.get(i.key) ?? { planned: 0, actual: 0 };
    groups.set(i.key, { planned: r2(g.planned + i.annual.planned), actual: r2(g.actual + i.annual.actual) });
  }
  return [...groups].map(([key, g]) => ({
    key,
    ...g,
    consumedPercent: percent(g.actual, g.planned),
    available: r2(g.planned - g.actual),
  }));
}

// --- Contrato da API ---------------------------------------------------------------------------

const ref = z.object({ id: z.string().uuid(), name: z.string() });
const totalsSchema = z.object({
  planned: z.number(),
  actual: z.number(),
  consumedPercent: z.number().nullable(),
  available: z.number(),
});
const monthRowSchema = z.object({
  month: z.number().int(),
  planned: z.number().nullable(),
  actual: z.number().nullable(),
  balance: z.number().nullable(),
  consumedPercent: z.number().nullable(),
  plannedAccumulated: z.number(),
  actualAccumulated: z.number(),
  balanceAccumulated: z.number(),
});

export const budgetItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  type: z.enum(BUDGET_TYPES),
  startDate: z.string(),
  endDate: z.string(),
  initiative: ref.nullable(),
  objective: ref.nullable(),
  archivedAt: z.string().nullable(),
  updatedAt: z.string(),
  period: z.object({
    status: z.enum(BUDGET_PERIOD_STATUSES),
    totalDays: z.number().int(),
    daysRemaining: z.number().int(),
  }),
  /** Ano dos valores mês a mês (no detalhamento, o pedido limitado à vigência do item). */
  year: z.number().int(),
  /** Meses do ano pedido dentro da vigência (os únicos que recebem valores). */
  openMonths: z.array(z.number().int().min(1).max(12)),
  months: z.array(monthRowSchema),
  annual: totalsSchema,
  total: totalsSchema,
});
export type BudgetItem = z.infer<typeof budgetItemSchema>;

export const budgetListQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  archived: z.enum(['true', 'false']).optional(),
});

export const budgetListSchema = z.object({
  year: z.number().int(),
  years: z.array(z.number().int()),
  /** Categorias já usadas no projeto (sugestões do cadastro). */
  categories: z.array(z.string()),
  items: z.array(budgetItemSchema),
  totalsByCategory: z.array(totalsSchema.extend({ key: z.string() })),
  totalsByType: z.array(totalsSchema.extend({ key: z.enum(BUDGET_TYPES) })),
  /** Total do ano (todos os itens da lista) e mês a mês somado. */
  annual: totalsSchema,
  months: z.array(z.object({ month: z.number().int(), planned: z.number(), actual: z.number() })),
});
export type BudgetList = z.infer<typeof budgetListSchema>;

export const budgetOptionsSchema = z.object({
  initiatives: z.array(ref),
  objectives: z.array(ref),
});
export type BudgetOptions = z.infer<typeof budgetOptionsSchema>;

export const budgetItemQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});
